import { ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react'
import { Params } from 'react-router-dom'

import { IconType } from '@factorialco/f0-react'
import { RecordType } from '@factorialco/f0-react/dist/experimental'
import {
  ChartLine,
  Group,
  LogoAvatar,
  Office,
  People,
  Person,
  Plus,
  SearchPerson,
  Suitcase,
} from '@factorialco/f0-react/icons/app'
import {
  JobCatalogCompetencyAttribute,
  JobCatalogWorkingConditionsAttribute,
} from 'generated/resources/datatable.graphql'
import type { JobCatalogEnumsJobCatalogTreeNodeTypesEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { type JobCatalogNodeData } from 'modules/job_catalog/components/JobCatalogTree/types'

import { Salary } from './salary'

export const DEFAULT_CATEGORIES = {
  OVERVIEW: 'overview',
  COMPETENCIES: 'competency',
  WORKING_CONDITIONS: 'WorkingConditions',
} as const

export type CategoryType = (typeof DEFAULT_CATEGORIES)[keyof typeof DEFAULT_CATEGORIES]

export const NODE_TYPES = {
  FAMILY: 'family',
  FUNCTION: 'function',
  ROLE: 'role',
  LEVEL: 'level',
  ROOT: 'root',
  COMPANY: 'company',
} as const

export const NODE_UUID_TYPES = {
  FAMILY: 'jobcatalog_treefamily',
  FUNCTION: 'jobcatalog_treefunction',
  ROLE: 'jobcatalog_treerole',
  LEVEL: 'jobcatalog_treelevel',
  ROOT: 'jobcatalog_treeroot',
  COMPANY: 'jobcatalog_treeroot',
}

export type NodeType = (typeof NODE_TYPES)[keyof typeof NODE_TYPES]
export type NodeUUIDType = (typeof NODE_UUID_TYPES)[keyof typeof NODE_UUID_TYPES]

export interface NodeFormValues {
  name: string
  description: string
  children?: string[]
}

export interface GroupedData extends RecordType {
  uuid: string
  name: string
  salary: string
  levels?: string[]
}

export interface TreeNode {
  id: number
  node_uuid: string
  type: string
  ancestor_id: string | null
  name: string
  description: string | null
  children: TreeNode[]
  parent?: TreeNode
}

export interface NodeChildren {
  id: number
  name: string
  type: string
  attributesJson: string
}

export interface WorkingConditionItem {
  name: string
  value: string
}

interface CustomEmptyState {
  emoji?: string
  title: string
  description?: string
  actions?: {
    label: string
    onClick: () => void
    variant?: 'default' | 'outline' | 'promote'
    icon?: IconType
  }[]
}

export interface EmptyStateContent {
  title: string
  description: string
  icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
  >
}

export interface ParentChain {
  rootId?: string
  familyId?: string
  functionId?: string
  roleId?: string
}

const extractSimpleType = (type?: string): string => type?.split('::')[1] || type || ''

export const normalizeNodeType = (type?: string): JobCatalogNodeData['type'] => {
  const raw = extractSimpleType(type).toLowerCase()
  if (raw.includes('family')) return 'family'
  if (raw.includes('function')) return 'function'
  if (raw.includes('role')) return 'role'
  if (raw.includes('level')) return 'level'
  return 'root'
}

export const isNodeType = (value: string): value is NodeType => {
  const normalized = normalizeNodeType(value)
  return Object.values(NODE_TYPES).includes(normalized)
}

export const getNodeId = (uuid?: string | null): number => {
  if (!uuid) return 0
  const parts = uuid.includes('::') ? uuid.split('::')[1] : uuid
  return parseInt(parts?.split('-')[1] || '0', 10)
}

/**
 * Validates and extracts a valid node ID from route parameters.
 * Filters out invalid values like "null", "undefined", or empty strings.
 */
export const getValidNodeIdFromParams = (
  levelId?: string,
  roleId?: string,
  functionId?: string,
  familyId?: string,
  rootId?: string
): string => {
  const isValidId = (id: string | undefined): boolean => {
    return !!id && id !== 'null' && id !== 'undefined' && id.trim() !== ''
  }
  return [levelId, roleId, functionId, familyId, rootId].find(isValidId) || ''
}

export const getNodeType = (uuid?: string): string => {
  if (!uuid) return ''
  return uuid?.split('-')[0] || ''
}

export const getChildNodeType = (uuid?: string): JobCatalogEnumsJobCatalogTreeNodeTypesEnum => {
  const type = getNodeType(uuid)
  const normalizedType = normalizeNodeType(type)
  switch (normalizedType) {
    case NODE_TYPES.ROOT:
      return 'jobcatalog_treefamily'
    case NODE_TYPES.FAMILY:
      return 'jobcatalog_treefunction'
    case NODE_TYPES.FUNCTION:
      return 'jobcatalog_treerole'
    case NODE_TYPES.ROLE:
      return 'jobcatalog_treelevel'
    default:
      return 'jobcatalog_treelevel'
  }
}

export const getTextFromNodeTypeSummary = (type: string, count?: number): string => {
  type = normalizeNodeType(type)
  switch (type) {
    case NODE_TYPES.FAMILY:
      return count !== undefined
        ? i18n.tp('job_catalog.nodes.types.summary.family', { count })
        : i18n.t('job_catalog.nodes.types.summary.family.one')
    case NODE_TYPES.FUNCTION:
      return count !== undefined
        ? i18n.tp('job_catalog.nodes.types.summary.function', { count })
        : i18n.t('job_catalog.nodes.types.summary.function.one')
    case NODE_TYPES.ROLE:
      return count !== undefined
        ? i18n.tp('job_catalog.nodes.types.summary.role', { count })
        : i18n.t('job_catalog.nodes.types.summary.role.one')
    case NODE_TYPES.LEVEL:
      return count !== undefined
        ? i18n.tp('job_catalog.nodes.types.summary.level', { count })
        : i18n.t('job_catalog.nodes.types.summary.level.one')
    default:
      return ''
  }
}

export const getTextFromNodeTypePlural = (type: string): string => {
  type = normalizeNodeType(type)
  switch (type) {
    case NODE_TYPES.FAMILY:
      return i18n.t('job_catalog.nodes.types.family.plural')
    case NODE_TYPES.FUNCTION:
      return i18n.t('job_catalog.nodes.types.function.plural')
    case NODE_TYPES.ROLE:
      return i18n.t('job_catalog.nodes.types.role.plural')
    case NODE_TYPES.LEVEL:
      return i18n.t('job_catalog.nodes.types.level.plural')
    default:
      return ''
  }
}

export const getTextFromNodeType = (type: string, count?: number): string => {
  type = normalizeNodeType(type)
  switch (type) {
    case NODE_TYPES.FAMILY:
      if (count === undefined) return i18n.t('job_catalog.nodes.types.summary.family.one')
      if (count === 1) return i18n.t('job_catalog.nodes.types.family.one_label', { count })

      return i18n.t('job_catalog.nodes.types.family.multiple_label', { count })
    case NODE_TYPES.FUNCTION:
      if (count === undefined) return i18n.t('job_catalog.nodes.types.summary.function.one')
      if (count === 1) return i18n.t('job_catalog.nodes.types.function.one_label', { count })

      return i18n.t('job_catalog.nodes.types.function.multiple_label', { count })
    case NODE_TYPES.ROLE:
      if (count === undefined) return i18n.t('job_catalog.nodes.types.summary.role.one')
      if (count === 1) return i18n.t('job_catalog.nodes.types.role.one_label', { count })

      return i18n.t('job_catalog.nodes.types.role.multiple_label', { count })
    case NODE_TYPES.LEVEL:
      if (count === undefined) return i18n.t('job_catalog.nodes.types.summary.level.one')
      if (count === 1) return i18n.t('job_catalog.nodes.types.level.one_label', { count })

      return i18n.t('job_catalog.nodes.types.level.multiple_label', { count })
    default:
      return 'Company'
  }
}

export const nodeTypeChildren = (type: NodeType): string => {
  type = normalizeNodeType(type)
  switch (type) {
    case NODE_TYPES.ROOT:
      return i18n.t('job_catalog.nodes.types.family.multiple')
    case NODE_TYPES.FAMILY:
      return i18n.t('job_catalog.nodes.types.function.multiple')
    case NODE_TYPES.FUNCTION:
      return i18n.t('job_catalog.nodes.types.role.multiple')
    case NODE_TYPES.ROLE:
      return i18n.t('job_catalog.nodes.types.level.multiple')
    default:
      return 'Root'
  }
}

export const nodeTypeIcon = (type: NodeType) => {
  type = normalizeNodeType(type)
  switch (type) {
    case NODE_TYPES.FAMILY:
      return People
    case NODE_TYPES.FUNCTION:
      return Group
    case NODE_TYPES.ROLE:
      return Person
    case NODE_TYPES.LEVEL:
      return SearchPerson
    default:
      return Office
  }
}

export const getOverviewActionLabel = (type: NodeType): string => {
  switch (type) {
    case NODE_TYPES.FAMILY:
      return i18n.t(`job_catalog.nodes.empty_state.overview.family.actions.new`)
    case NODE_TYPES.FUNCTION:
      return i18n.t(`job_catalog.nodes.empty_state.overview.function.actions.new`)
    case NODE_TYPES.ROLE:
      return i18n.t(`job_catalog.nodes.empty_state.overview.role.actions.new`)
    case NODE_TYPES.LEVEL:
      return i18n.t(`job_catalog.nodes.empty_state.overview.level.actions.new`)
    default:
      return ''
  }
}

export const getOverviewEmptyStateContent = ({
  type,
  handleAction,
}: {
  type: NodeType
  handleAction: () => void
}): CustomEmptyState | undefined => {
  switch (type) {
    case NODE_TYPES.FAMILY:
      return {
        emoji: '💼',
        title: i18n.t(`job_catalog.nodes.empty_state.overview.family.title`),
        description: i18n.t(`job_catalog.nodes.empty_state.overview.family.description`),
        actions: [
          {
            label: getOverviewActionLabel(NODE_TYPES.FAMILY),
            onClick: handleAction,
            icon: Plus,
          },
        ],
      }
    case NODE_TYPES.FUNCTION:
      return {
        emoji: '💼',
        title: i18n.t(`job_catalog.nodes.empty_state.overview.function.title`),
        description: i18n.t(`job_catalog.nodes.empty_state.overview.function.description`),
        actions: [
          {
            label: getOverviewActionLabel(NODE_TYPES.FUNCTION),
            onClick: handleAction,
            icon: Plus,
          },
        ],
      }
    case NODE_TYPES.ROLE:
      return {
        emoji: '💼',
        title: i18n.t(`job_catalog.nodes.empty_state.overview.role.title`),
        description: i18n.t(`job_catalog.nodes.empty_state.overview.role.description`),
        actions: [
          {
            label: getOverviewActionLabel(NODE_TYPES.ROLE),
            onClick: handleAction,
            icon: Plus,
          },
        ],
      }
    case NODE_TYPES.LEVEL:
      return {
        emoji: '💼',
        title: i18n.t(`job_catalog.nodes.empty_state.overview.level.title`),
        description: i18n.t(`job_catalog.nodes.empty_state.overview.level.description`),
        actions: [
          {
            label: getOverviewActionLabel(NODE_TYPES.LEVEL),
            onClick: handleAction,
            icon: Plus,
          },
        ],
      }
    default:
      return undefined
  }
}

export const getEmptyStateContent = (type?: string): EmptyStateContent => {
  switch (type) {
    case DEFAULT_CATEGORIES.OVERVIEW:
      return {
        title: i18n.t('job_catalog.nodes.empty_state.roles.title'),
        description: i18n.t('job_catalog.nodes.empty_state.roles.description'),
        icon: LogoAvatar,
      }

    case DEFAULT_CATEGORIES.COMPETENCIES:
      return {
        title: i18n.t('job_catalog.nodes.empty_state.competency.title'),
        description: i18n.t('job_catalog.nodes.empty_state.competency.description'),
        icon: ChartLine,
      }

    default:
      return {
        title: i18n.t('job_catalog.nodes.empty_state.condition.title'),
        description: i18n.t('job_catalog.nodes.empty_state.condition.description'),
        icon: Suitcase,
      }
  }
}

export const countNodeTypes = (nodes: TreeNode[]): Record<string, number> => {
  const counts: Record<string, number> = {}

  const traverse = (node: TreeNode) => {
    const type = extractSimpleType(node.type)
    if (type) counts[type] = (counts[type] ?? 0) + 1
    node.children?.forEach(traverse)
  }

  nodes.forEach(traverse)
  return counts
}

export const parseTreeNodes = (
  rawTree: { id: number; serializedTree: string } | undefined
): TreeNode[] => {
  const serializedTree = JSON.parse(rawTree?.serializedTree || '{}')
  return Array.isArray(serializedTree) ? serializedTree : [serializedTree]
}

export const findNodeByUuid = (nodes: TreeNode[], uuid: string): TreeNode | null => {
  for (const node of nodes) {
    if (node.node_uuid === uuid) return node
    const found = findNodeByUuid(node.children, uuid)
    if (found) return found
  }
  return null
}

export const getNavigationForTypeFromFunction = (
  navigation: Navigation<NavigationPaths>,
  type: string
) => {
  const normalizedType = normalizeNodeType(type)
  switch (normalizedType) {
    case NODE_TYPES.ROOT:
      return navigation.employees.jobCatalog.root
    case NODE_TYPES.FAMILY:
      return navigation.employees.jobCatalog.root.family
    case NODE_TYPES.FUNCTION:
      return navigation.employees.jobCatalog.root.family.function
    case NODE_TYPES.ROLE:
      return navigation.employees.jobCatalog.root.family.function.role
    case NODE_TYPES.LEVEL:
      return navigation.employees.jobCatalog.root.family.function.role.nodeLevel
    default:
      return navigation.employees.jobCatalog.root.family
  }
}

export const getNavigationForTypeFromUuid = (
  navigation: Navigation<NavigationPaths>,
  uuid: string
) => {
  const type = getNodeType(uuid)

  return getNavigationForTypeFromFunction(navigation, type)
}

export const getNavigationParentRoute = (
  nodeUuid: string,
  type: NodeUUIDType,
  navigation: Navigation<NavigationPaths>,
  params: Readonly<Params<string>>
): string => {
  const parentParams = getParentNavigationParams(nodeUuid, params)
  let parentNavigation
  switch (type) {
    case NODE_UUID_TYPES.FAMILY:
      parentNavigation = navigation.employees.jobCatalog.root
      break
    case NODE_UUID_TYPES.FUNCTION:
      parentNavigation = navigation.employees.jobCatalog.root.family
      break
    case NODE_UUID_TYPES.ROLE:
      parentNavigation = navigation.employees.jobCatalog.root.family.function
      break
    case NODE_UUID_TYPES.LEVEL:
      parentNavigation = navigation.employees.jobCatalog.root.family.function.role
      break
    default:
      parentNavigation = navigation.employees.jobCatalog.root
  }
  return parentNavigation.fullPath(parentParams)
}

export function getNodeTypeFromParams(params: {
  familyId?: string
  functionId?: string
  roleId?: string
  levelId?: string
}): NodeType {
  if (params.levelId) return NODE_TYPES.LEVEL
  if (params.roleId) return NODE_TYPES.ROLE
  if (params.functionId) return NODE_TYPES.FUNCTION
  if (params.familyId) return NODE_TYPES.FAMILY
  return NODE_TYPES.ROOT
}

export const formatUuidToNodeType = (type: string): JobCatalogEnumsJobCatalogTreeNodeTypesEnum => {
  const normalizedType = normalizeNodeType(type)
  switch (normalizedType) {
    case 'family':
      return 'jobcatalog_treefamily'
    case 'function':
      return 'jobcatalog_treefunction'
    case 'role':
      return 'jobcatalog_treerole'
    case 'level':
      return 'jobcatalog_treelevel'
    default:
      return 'jobcatalog_treeroot'
  }
}

type JobCatalogChildNode = {
  id: number
  name?: string | null
  attributesJson?: string | null
}

type Attribute = NodeAttribute | ChildAttribute
//TODO: unfortunately this abstraction is needed until the backend maps the children nodes better

export type NodeAttribute = {
  id: number
  nodeName?: string
  type: string
  definedAt: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
  takesPrecedence: boolean
  serializedValue: string
  takes_precedence?: never
  serialized_value?: never
  valueWorkingConditions?: JobCatalogWorkingConditionsAttribute | null
  valueCompetency?: JobCatalogCompetencyAttribute | null
}

export type ChildAttribute = {
  id: number
  nodeName?: string
  nodeId?: string
  type: string
  takes_precedence: boolean
  serialized_value: string
  attribute_id?: string
  takesPrecedence?: never
  serializedValue?: never
}

export const getSalariesFromAttributes = (nodeAttributes: Attribute[]): Salary[] => {
  return nodeAttributes.filter(isSalary).map(toSalary)
}

export const getAllSalariesFromChildrenAttributes = (nodes: JobCatalogChildNode[]): Salary[] => {
  const salaries: Salary[] = []
  nodes.forEach((node) => {
    const nodeSalaries = getSalariesFromChildAttributes(node.attributesJson ?? '[]').map(
      (salary) => ({
        ...salary,
        ...(node.name && { nodeName: node.name }),
        ...(node.id && { nodeId: node.id }),
      })
    )
    salaries.push(...nodeSalaries)
  })
  return salaries
}

export const getSalariesFromChildAttributes = (attributesJson: string): Salary[] => {
  const attributes = JSON.parse(attributesJson)
  return getSalariesFromAttributes(attributes)
}

const isSalary = (nodeAttribute: Attribute): boolean => {
  return (
    nodeAttribute.type === 'salary_range' &&
    !!(nodeAttribute.takesPrecedence || nodeAttribute.takes_precedence)
  )
}

const toSalary = (salaryAttribute: Attribute): Salary => {
  if (isNodeAttribute(salaryAttribute))
    return { ...JSON.parse(salaryAttribute.serializedValue), id: salaryAttribute.id }
  else return { ...JSON.parse(salaryAttribute.serialized_value), id: salaryAttribute.id }
}

const isNodeAttribute = (attr: Attribute): attr is NodeAttribute =>
  attr.serializedValue != undefined

export const getParentChain = (node?: TreeNode): ParentChain => {
  const chain: Record<string, string> = {}
  let current = node?.parent ?? node

  while (current) {
    const type = current.type.toLowerCase()
    if (type.includes('root')) {
      chain.rootId = current.node_uuid
    } else if (type.includes('family')) {
      chain.familyId = current.node_uuid
    } else if (type.includes('function')) {
      chain.functionId = current.node_uuid
    } else if (type.includes('role')) {
      chain.roleId = current.node_uuid
    }
    current = current.parent
  }

  return chain
}
export const getChildTypeText = (nodeId: string, itemCount?: number) => {
  const normalizedNodeType = normalizeNodeType(nodeId?.split('-')[0] || '')
  switch (normalizedNodeType) {
    case NODE_TYPES.ROOT:
      return itemCount !== undefined
        ? itemCount === 1
          ? i18n.t('job_catalog.nodes.types.family.one_label', { count: itemCount })
          : i18n.t('job_catalog.nodes.types.family.multiple_label', { count: itemCount })
        : i18n.t('job_catalog.nodes.types.family.one')
    case NODE_TYPES.FAMILY:
      return itemCount !== undefined
        ? itemCount === 1
          ? i18n.t('job_catalog.nodes.types.function.one_label', { count: itemCount })
          : i18n.t('job_catalog.nodes.types.function.multiple_label', { count: itemCount })
        : i18n.t('job_catalog.nodes.types.function.one')
    case NODE_TYPES.FUNCTION:
      return itemCount !== undefined
        ? itemCount === 1
          ? i18n.t('job_catalog.nodes.types.role.one_label', { count: itemCount })
          : i18n.t('job_catalog.nodes.types.role.multiple_label', { count: itemCount })
        : i18n.t('job_catalog.nodes.types.role.one')
    case NODE_TYPES.ROLE:
      return itemCount !== undefined
        ? itemCount === 1
          ? i18n.t('job_catalog.nodes.types.level.one_label', { count: itemCount })
          : i18n.t('job_catalog.nodes.types.level.multiple_label', { count: itemCount })
        : i18n.t('job_catalog.nodes.types.level.one')
    default:
      return ''
  }
}

export const maximizeClickPath = (
  parentChain: ParentChain,
  normalizedType: string,
  nodeUuid: string,
  navigation: Navigation<NavigationPaths>,
  rawType: string
) => {
  const params = {
    rootId: parentChain.rootId ?? (normalizedType === NODE_TYPES.ROOT ? nodeUuid : ''),
    familyId: parentChain.familyId ?? (normalizedType === NODE_TYPES.FAMILY ? nodeUuid : ''),
    functionId: parentChain.functionId ?? (normalizedType === NODE_TYPES.FUNCTION ? nodeUuid : ''),
    roleId: parentChain.roleId ?? (normalizedType === NODE_TYPES.ROLE ? nodeUuid : ''),
    levelId: normalizedType === NODE_TYPES.LEVEL ? nodeUuid : '',
  }

  const nav = getNavigationForTypeFromFunction(navigation, rawType)
  return nav.fullPath(params)
}

export const formatWorkingConditionValue = (item: WorkingConditionItem): string => {
  switch (item.name) {
    case 'max_legal_yearly_hours_cents':
      return `${parseInt(item.value) / 100} hours`
    case 'bank_holiday_treatment':
      switch (item.value) {
        case 'workable':
          return 'Workable'
        case 'non_workable':
          return 'Non-workable'
        default:
          return item.value
      }
    default:
      return item.value
  }
}

export const formatConditionName = (name: string): string => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace('Cents', '')
}

export const getCategoryTranslation = (category: CategoryType, type?: string): string => {
  switch (category) {
    case DEFAULT_CATEGORIES.OVERVIEW:
      if (type === 'function') {
        return i18n.t('job_catalog.nodes.types.role.multiple')
      }
      if (type === 'role') {
        return i18n.t('job_catalog.nodes.types.level.multiple')
      }
      if (type === 'root') {
        return i18n.t('job_catalog.nodes.types.family.multiple')
      }
      if (type === 'family') {
        return i18n.t('job_catalog.nodes.types.function.multiple')
      }
    case DEFAULT_CATEGORIES.COMPETENCIES:
      return i18n.t('job_catalog.nodes.competency.multiple')
    case DEFAULT_CATEGORIES.WORKING_CONDITIONS:
      return i18n.t('job_catalog.nodes.work_conditions')
    default:
      return category
  }
}

const getParentNavigationParams = (
  nodeId: string,
  params: Readonly<Params<string>>
): NodeNavigationParams => {
  const { rootId, familyId, functionId, roleId } = params
  return {
    rootId: rootId ?? '',
    familyId: nodeId === familyId ? '' : familyId ?? '',
    functionId: [familyId, functionId].includes(nodeId) ? '' : functionId ?? '',
    roleId: [familyId, functionId, roleId].includes(nodeId) ? '' : roleId ?? '',
    levelId: '',
  }
}

type NodeNavigationParams = {
  rootId: string
  familyId: string
  functionId: string
  roleId: string
  levelId: string
}

// Node hierarchy order for comparison (lower number = higher in hierarchy)
const NODE_HIERARCHY_ORDER: Record<NodeType, number> = {
  [NODE_TYPES.ROOT]: 0,
  [NODE_TYPES.FAMILY]: 1,
  [NODE_TYPES.FUNCTION]: 2,
  [NODE_TYPES.ROLE]: 3,
  [NODE_TYPES.LEVEL]: 4,
  [NODE_TYPES.COMPANY]: 0, // Company is treated same as ROOT
}

/**
 * Determines if a delete button should be shown for a node attribute.
 * The delete button should only be shown if the current node level is the same
 * as or higher in the hierarchy than the level where the attribute was defined.
 *
 * @param currentNodeType - The type of the current node being viewed
 * @param definedAtNodeType - The type of the node where the attribute was defined
 * @returns true if delete button should be shown, false otherwise
 */
export const shouldShowDeleteButton = (
  currentNodeType: NodeType,
  definedAtNodeType: NodeType
): boolean => {
  const currentOrder = NODE_HIERARCHY_ORDER[currentNodeType] ?? 999
  const definedAtOrder = NODE_HIERARCHY_ORDER[definedAtNodeType] ?? 999

  // Can only delete if current node is at the same level or higher in hierarchy
  return currentOrder <= definedAtOrder
}
